'use client';

import React, { useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useFormState } from 'react-hook-form';

import { useComponentLogger } from '@/lib/component-logger';
import { fetchAPIData, fetchDataFromEndpoint } from '@/lib/dataHandlers/DataFetchers';
import { escapeRegex } from '@/lib/dataHandlers/StringHelpers';
import DataContext from '@/contexts/DataContext';
import { useSettings } from '@/hooks/use-settings';

import { baseMenuConfig } from './baseMenuConfig';
import { HorizontalLayout } from './horizontal/horizontal-layout';
import { VerticalLayout } from './vertical/vertical-layout';

export function DynamicLayout({ children }) {
  const logger = useComponentLogger("Dynamic Layout");
  const { settings } = useSettings();
  const location = useLocation();
  // const { projects, setProjects } = useContext(DataContext);
  const { currentProject, setCurrentProject, setProjects, setAppsAndViews, setMenuConfig, setCurrentView, setViewParams, setPrelimParamCount, setSelectedViewParams, historianParams, setHistorianParams } = useContext(DataContext);


  useEffect(() => {
    const fetchProjectIcon = async (projectID) => {
    // logger.debug('Fetching icon for project ID: ', projectID);
      const url = `https://data.clistek.io/api/${projectID}`;
      const expr = `/file/proj/${projectID}/io/mobileAvatar.png`;

      // logger.debug("fetching proj icons", expr);
      const data = await fetchAPIData(url, expr, false);
      // logger.debug('proj icon data', data, projectID);
      //since the img is received as based64 in the res
      if (data && typeof data === 'string' && data.startsWith('iVBOR')) {
        // I added this to handle the different schemes for images
        return `data:image/png;base64,${data}`;
      }
      return '';
    };
  

    const fetchProjects = async () => {
      try {
        const data = await fetchDataFromEndpoint('/api/getProjects');
        if (data?.rows) {
          const projectMap = await Promise.all(
            data.rows.map(async (project) => {
              if (project.dis && project.id?.val) {
                const avatar = await fetchProjectIcon(project.id.val.replace('p:', ''));
                // logger.debug("avatar: ", avatar);
                return {
                  id: project.id.val,
                  name: project.dis,
                  avatar: avatar || "",
                };
              }
              return null;
            })
          );

          const filteredProjectMap = projectMap?.filter((project) => project !== null);
        // logger.debug('Fetched project data:', filteredProjectMap);
          setProjects(filteredProjectMap);
          localStorage.setItem("projects", JSON.stringify(filteredProjectMap));

          if (filteredProjectMap.length === 1) {
            setCurrentProject(filteredProjectMap[0]);
          }
        } else {
          setProjects([]);
        }
      } catch (error) {
        logger.error('Error fetching projects:', error);
        setProjects([]);
      }
    };

    fetchProjects();
  }, [setCurrentProject, setProjects]);

  /**
   *
   * @param {*} label
   * @param {*} mobileApps
   * @param {*} mobileViews
   * @returns updated mobileApps and Views with icon property filled as an svg code
   */
  const updateObjectsWithIcons = async (label, mobileApps, mobileViews) => {
    // logger.debug('testing received params: ', label, mobileApps, mobileViews);
    const updatedMobileApps = await Promise.all(
      mobileApps?.map(async (app) => {
        const icon = await fetchIcon(label, app.icon);
        return { ...app, icon };
      })
    );

    const updatedMobileViews = await Promise.all(
      mobileViews?.map(async (view) => {
        const icon = await fetchIcon(label, view.icon);
        return { ...view, icon };
      })
    );

    return { updatedMobileApps, updatedMobileViews };
  };

  /**
   * Testing new ways to fetch icons
   * @param label will be the selected project
   * @param iconName will be the name of the icon
   * makes an api call return an svg code for each icon
   */
  const fetchIcon = async (label, iconName) => {
    if (!label || !iconName) return;
    // logger.debug('fetchIcon params: ', label, iconName);
    const url = `https://data.clistek.io/api/${label}/file/icon/${iconName}`;
    try {
      const response = await fetch('/api/iconsFetcher', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          url: url,
        }),
      });

      // logger.debug("iconsurl: ", response);
      if (response.ok) {
        // const data = await response.blob();
        //had no options i had to .text() to have it work
        const returnedSvgCode = await response.text();
        const parseSvg2Json = JSON.parse(returnedSvgCode);

        return parseSvg2Json;
      } else {
        throw new Error(response.statusText);
      }
    } catch (error) {
      console.error('Error fetching icon:', error);
      return '';
    }
  };

  useEffect(() => {
    const fetchAppsAndViews = async () => {
    // logger.debug('new project', currentProject);
      if (currentProject.id === null || currentProject.id === undefined) return;

      const parsedProject = currentProject.id.replace('p:', '');
      const url = `https://data.clistek.io/api/${parsedProject}/eval?expr=`;
      // const expr = `readAll(mobileView+or+mobileApp)`;
      const expr = `readAll(mobileViewV2+or+mobileApp)`;

      // logger.debug("fetching apps & views", expr);
      const data = await fetchAPIData(url, expr, false);
      logger.debug('app and view data', data);

      if (data?.rows) {
        // Sort data into apps and views
        let apps = [];
        let views = [];

        data.rows.map((row) => {
          if (row.mobileApp) {
            apps.push(row);
          }
          if (row.mobileAppRef) {
            try {
              // logger.debug(`${row.dis} viewData before:`, row.viewData);
              row.viewData = JSON.parse(row.viewData);
              // logger.debug(`${row.dis} viewData after:`, row.viewData);
            } catch (error) {
              console.error(`Error parsing ${row.dis} viewData as JSON:`, error);
            }
            views.push(row);
          }
        });

        // logger.debug('raw apps and views', apps, views);

        // fetch icons
        // const { updatedApps, updatedViews } = await updateObjectsWithIcons(currentProject.name, apps.sort((a,b) =>a.order - b.order), views.sort((a, b) => a.order - b.order));
        // logger.debug("updatedApps", updatedApps);

        // const processedAppsAndViews = updatedApps?.map((app) => {
        let processedViews = [];

        views?.map((view) => {
          // logger.debug('curView', view);
 
          let appURL = `/?proj=${parsedProject}&appID=${view.mobileAppRef?.val}&viewID=${view.id?.val}`;
          let viewURL = `/?proj=${parsedProject}&viewID=${view.id?.val}`
          let url = `^(${escapeRegex(appURL)}|${escapeRegex(viewURL)})`
          let regex = new RegExp(`^(${escapeRegex(appURL)}|${escapeRegex(viewURL)})`);
          logger.debug('view url regex', url, regex)

          // sort view groups by order
          // view.viewData.objects.sort((a, b) => a.order - b.order);
          view.viewData?.groups?.sort((a, b) => a.order - b.order);
          
          processedViews.push({
            order: view.order,
            id: view.id?.val,
            name: view.dis,
            href: viewURL,
            mobileAppRef: view.mobileAppRef,
            icon: view.mobileIcon || '',
            viewData: view.viewData,
            // matcher: { type: 'startsWith', href: defaultURL,},
            matcher: { type: 'regex', href: appURL, regex: regex },
            // matcher: { type: 'view', href: appURL, appURL: appURL, viewURL: viewURL },
          });
        });

        let processedAppsAndViews = apps?.map((app) => {
          // logger.debug('curApp:', app);
          // const appViews = updatedViews.filter(
          let appViews = processedViews.filter(
            (view) => view.mobileAppRef?.val === app.id?.val // nest view under respective app
          );

          // appViews = appViews?.sort((a, b) => a.order - b.order) || [];

          appViews = appViews?.sort((a, b) => {
            // Primary sort by "order"
            const orderDiff = (a.order ?? 0) - (b.order ?? 0);
            if (orderDiff !== 0) return orderDiff;

            // Secondary sort alphabetically by "dis"
            return a.name?.localeCompare(b.name);
          }) || [];
          logger.debug('sorted views', appViews);

          return {
            id: app.id?.val,
            name: app.dis, // appName
            // icon: app.icon, // icon svg
            icon: app.mobileIcon || '',
            items: appViews, // arr of views
            projectID: currentProject.id, // the project the app belongs to
            order: app.order,
          };
        });

        // sort apps by their order
        let sortedAppsAndViews = processedAppsAndViews?.sort((a, b) => a.order - b.order) || [];

      // logger.debug('app data', sortedAppsAndViews);
        setAppsAndViews(sortedAppsAndViews);
        localStorage.setItem("appsAndViews", JSON.stringify(sortedAppsAndViews));
        buildMenuConfig(sortedAppsAndViews);
      }
    };

    const buildMenuConfig = (appAndViewData) => {
      let config = [];
      let baseConfig = baseMenuConfig[0];
      // logger.debug('build menu', baseConfig, currentProject);
      if (currentProject !== null && currentProject !== undefined && currentProject.id) {
        const parsedProject = currentProject.id.replace('p:', '');
        for (const item of baseConfig.items) {
          if (item.id === 'alerts') {
            item.href = `/alerts?proj=${parsedProject}`;
          } else if (item.id === 'historian') {
            item.href = `/historian?proj=${parsedProject}`;
          }  else if (item.id === 'home') {
            let homeURL = `/?proj=${parsedProject}`;
            item.matcher.href = homeURL;
            item.href = homeURL;
          }
        }
      }
      config.push(baseConfig);
      // logger.debug('new base', baseConfig);

      if (baseMenuConfig.length > 1) {
        for (let i = 1; i < baseMenuConfig.length; i++) {
          config.push(baseMenuConfig[i]);
        }
      }
      
      config.push({
        id: 'apps',
        name: 'Apps',
        items: appAndViewData,
      });
      setMenuConfig(config);
      // logger.debug('menu config', config);
    };

    setCurrentView(null);
    setViewParams([]);
    setPrelimParamCount(0);
    setSelectedViewParams({});
    setHistorianParams({});
    fetchAppsAndViews();
  }, [currentProject]);

  useEffect(() => {
    logger.debug('layout location change', location);
    if (location.pathname !== '/historian' && Object.keys(historianParams)?.length > 0) {
      logger.debug('reset: historian')
      setHistorianParams({});
    }
  }, [location])


  return settings.layout === 'horizontal' ? (
    <HorizontalLayout>{children}</HorizontalLayout>
  ) : (
    <VerticalLayout>{children}</VerticalLayout>
  );
}
